import { http } from '@/core/api'

const DEFAULT_INTERVAL_MS = 2000
const MAX_INTERVAL_MS = 5000
const DEFAULT_TIMEOUT_MS = 300_000
/** If still «queued» this long, Horizon likely is not consuming `excel_heavy`. */
const QUEUED_STUCK_MS = 45_000

export type ExcelHeavyOperationStatus = 'queued' | 'processing' | 'ready' | 'failed' | string

export type ExcelHeavyOperationPayload<T = unknown> = {
  operation_id: number
  status: ExcelHeavyOperationStatus
  last_error?: string | null
  result?: T | null
  result_ref_id?: number | null
  access_token?: string | null
  progress?: ExcelHeavyOperationProgress | null
  async?: boolean
}

export type ExcelHeavyOperationProgress = { done: number; total: number }

type WaitOptions = {
  intervalMs?: number
  timeoutMs?: number
  onProgress?: (progress: ExcelHeavyOperationProgress) => void
}

export class ExcelHeavyOperationError extends Error {
  readonly status = 422
  readonly response: {
    status: number
    data: {
      message: string
      errors?: Record<string, string[]>
      row_errors?: unknown
      error_code?: string
    }
  }

  constructor(payload: ExcelHeavyOperationPayload) {
    const message = String(payload.last_error || 'Excel operation failed')
    super(message)
    this.name = 'ExcelHeavyOperationError'
    const result =
      payload.result && typeof payload.result === 'object'
        ? (payload.result as Record<string, unknown>)
        : {}
    this.response = {
      status: 422,
      data: {
        message,
        errors: (result.errors as Record<string, string[]> | undefined) ?? undefined,
        row_errors: result.row_errors,
        error_code: typeof result.error_code === 'string' ? result.error_code : undefined,
      },
    }
  }
}

function isAsyncOperation(data: unknown): data is ExcelHeavyOperationPayload {
  if (!data || typeof data !== 'object') return false
  const row = data as Record<string, unknown>
  const status = typeof row.status === 'string' ? row.status : ''
  const opId = typeof row.operation_id === 'number' ? row.operation_id : 0
  return (
    opId > 0 &&
    (row.async === true || status === 'queued' || status === 'processing')
  )
}

export async function getExcelHeavyOperation<T = unknown>(operationId: number) {
  const { data } = await http.get<{ data: ExcelHeavyOperationPayload<T> }>(
    `/excel-heavy-operations/${operationId}`,
    { timeout: 30_000 },
  )
  return data.data
}

async function getPublicExcelHeavyOperation<T = unknown>(operationId: number, accessToken: string) {
  const { data } = await http.get<{ data: ExcelHeavyOperationPayload<T> }>(
    `/excel-heavy-operations/${operationId}`,
    {
      baseURL: '/api/public',
      params: { access_token: accessToken },
      timeout: 30_000,
    },
  )
  return data.data
}

async function waitOperation<T>(
  fetch: () => Promise<ExcelHeavyOperationPayload<T>>,
  opts?: WaitOptions,
): Promise<T> {
  let intervalMs = opts?.intervalMs ?? DEFAULT_INTERVAL_MS
  const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const started = Date.now()
  let queuedSince: number | null = null

  while (Date.now() - started < timeoutMs) {
    const payload = await fetch()
    const status = String(payload?.status || '')
    if (status === 'ready') {
      return payload.result as T
    }
    if (status === 'failed') {
      throw new ExcelHeavyOperationError(payload)
    }
    if (payload?.progress && payload.progress.total > 0) {
      opts?.onProgress?.(payload.progress)
    }
    if (status === 'queued') {
      if (queuedSince === null) queuedSince = Date.now()
      if (Date.now() - queuedSince >= QUEUED_STUCK_MS) {
        throw new Error(
          'Hàng đợi Excel chưa được xử lý (queued quá lâu). Kiểm tra Horizon đang chạy supervisor excel_heavy rồi thử lại.',
        )
      }
    } else {
      queuedSince = null
    }

    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, intervalMs)
    })
    intervalMs = Math.min(MAX_INTERVAL_MS, Math.round(intervalMs * 1.25))
  }

  throw new Error('Excel operation timed out')
}

export async function waitExcelHeavyOperation<T = unknown>(
  operationId: number,
  opts?: WaitOptions & { accessToken?: string | null },
): Promise<T> {
  const token = opts?.accessToken?.trim() || ''
  if (token) {
    return waitOperation(() => getPublicExcelHeavyOperation<T>(operationId, token), opts)
  }
  return waitOperation(() => getExcelHeavyOperation<T>(operationId), opts)
}

/**
 * If the API returned a 202 async envelope, poll until ready and return the business result.
 * Otherwise return the payload as-is (sync queue / already finished).
 */
export async function resolveExcelHeavyResponse<T>(
  responseData: { data?: unknown } | unknown,
  opts?: WaitOptions,
): Promise<T> {
  const envelope =
    responseData && typeof responseData === 'object' && 'data' in (responseData as object)
      ? (responseData as { data: unknown }).data
      : responseData

  if (isAsyncOperation(envelope)) {
    const op = envelope as ExcelHeavyOperationPayload<T>
    if (op.status === 'ready' && op.result != null) {
      return op.result
    }
    if (op.status === 'failed') {
      throw new ExcelHeavyOperationError(op)
    }
    return waitExcelHeavyOperation<T>(op.operation_id, {
      ...opts,
      accessToken: op.access_token,
    })
  }

  return envelope as T
}

export async function downloadExcelHeavyBlob(operationId: number): Promise<Blob> {
  const { data } = await http.get<Blob>(`/excel-heavy-operations/${operationId}/download`, {
    responseType: 'blob',
    timeout: DEFAULT_TIMEOUT_MS,
  })
  return data
}

/**
 * List Excel export endpoints may return the file immediately (200) or a 202 async envelope.
 */
export async function resolveExcelHeavyBlobExport(
  response: { status: number; data: unknown; headers?: Record<string, unknown> },
  opts?: { intervalMs?: number; timeoutMs?: number },
): Promise<Blob> {
  const ct = String(response.headers?.['content-type'] ?? response.headers?.['Content-Type'] ?? '')
  if (response.status === 200 && !ct.includes('application/json') && response.data instanceof Blob) {
    return response.data
  }

  let envelope: unknown = response.data
  if (response.data instanceof Blob) {
    const text = await response.data.text()
    try {
      envelope = JSON.parse(text)
    } catch {
      return response.data
    }
  }

  const data =
    envelope && typeof envelope === 'object' && 'data' in (envelope as object)
      ? (envelope as { data: unknown }).data
      : envelope

  if (isAsyncOperation(data)) {
    const op = data as ExcelHeavyOperationPayload
    if (op.status === 'ready' || op.status === 'queued' || op.status === 'processing') {
      if (op.status !== 'ready') {
        await waitExcelHeavyOperation(op.operation_id, opts)
      }
      return downloadExcelHeavyBlob(op.operation_id)
    }
  }

  if (response.data instanceof Blob) return response.data
  throw new Error('Unexpected Excel export response')
}
